import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { UserCircle, X, Check } from 'lucide-react';
import { Avatar } from '@/components/ui/Avatar';
import { AssigneeAvatar } from '@/components/tasks/AssigneeAvatar';
import { cn } from '@/lib/utils';
import { inactiveAccountLabel, isInactiveAccount } from '@/lib/account-status';

const MENU_WIDTH = 208;

type Person = { id: string; name: string; avatarUrl?: string; accountStatus?: string };

export interface AssigneePickerProps {
  // Single-select (legacy)
  assignee?: Person | null;
  assigneeId?: string | null;
  onChange?: (assigneeId: string | null) => void;
  // Multi-select (co-assignees, all equal). When onChangeMultiple is provided
  // the picker renders an avatar stack + checkbox list and stays open on toggle.
  assignees?: Person[];
  onChangeMultiple?: (assigneeIds: string[]) => void;
  projectMembers: Array<{ user: Person }>;
  size?: 'xs' | 'sm';
  /** When true, swallow pointerdown so parent dnd-kit listeners don't start a drag. */
  stopDragPointerDown?: boolean;
}

export function AssigneePicker({
  assignee,
  assigneeId,
  onChange,
  assignees,
  onChangeMultiple,
  projectMembers,
  size = 'xs',
  stopDragPointerDown = true,
}: AssigneePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const multi = !!onChangeMultiple;
  const selectedIds = multi
    ? new Set((assignees ?? []).map((a) => a.id))
    : new Set(assigneeId ? [assigneeId] : []);
  const list = assignees ?? (assignee ? [assignee] : []);
  // Assigned people the member list no longer offers — suspended or removed
  // in Accounts. Listed in the menu with their badge so they can be unassigned.
  const memberIds = new Set(projectMembers.map((m) => m.user.id));
  const inactiveAssigned = list.filter((a) => isInactiveAccount(a.accountStatus) && !memberIds.has(a.id));
  const describe = (a: Person) =>
    isInactiveAccount(a.accountStatus) ? `${a.name} (${inactiveAccountLabel(a.accountStatus).toLowerCase()})` : a.name;

  const toggle = (id: string) => {
    if (multi) {
      const next = new Set(selectedIds);
      next.has(id) ? next.delete(id) : next.add(id);
      onChangeMultiple!([...next]);
      // keep the menu open for multi-select
    } else {
      onChange?.(id);
      setOpen(false);
      setSearch('');
    }
  };
  const clearAll = () => {
    if (multi) onChangeMultiple!([]);
    else onChange?.(null);
    setOpen(false);
    setSearch('');
  };

  const updatePosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const rightEdge = rect.left + MENU_WIDTH;
    const left = rightEdge > window.innerWidth - 8 ? Math.max(8, window.innerWidth - MENU_WIDTH - 8) : rect.left;
    setPos({ top: rect.bottom + 4, left });
  };

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => updatePosition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleOutside(e: MouseEvent) {
      const t = e.target as Node;
      if (menuRef.current && !menuRef.current.contains(t) && buttonRef.current && !buttonRef.current.contains(t)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onPointerDown={stopDragPointerDown ? (e) => e.stopPropagation() : undefined}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="rounded-full hover:ring-2 hover:ring-grey-200 transition-all flex items-center flex-shrink-0"
        title={list.length ? `Assigned to ${list.map(describe).join(', ')}` : 'Assign task'}
      >
        {list.length ? (
          <div className="flex items-center -space-x-1.5">
            {list.slice(0, 3).map((a) => (
              <AssigneeAvatar key={a.id} person={a} size={size} className="ring-1 ring-white" />
            ))}
            {list.length > 3 && (
              <span className="flex items-center justify-center rounded-full bg-grey-100 text-grey-600 text-[10px] font-medium w-5 h-5 ring-1 ring-white">
                +{list.length - 3}
              </span>
            )}
          </div>
        ) : (
          <UserCircle className={cn(size === 'xs' ? 'w-5 h-5' : 'w-6 h-6', 'text-grey-300')} />
        )}
      </button>

      {open && pos && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: MENU_WIDTH }}
          className="bg-white rounded-lg shadow-elevated border border-grey-200 py-1 z-50"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-2 pb-1">
            <input
              ref={searchRef}
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-grey-200 rounded focus:outline-none focus:border-primary"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {selectedIds.size > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); clearAll(); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-grey-500 hover:bg-grey-50 transition-colors"
              >
                <X className="w-4 h-4" />
                {multi ? 'Clear all' : 'Unassign'}
              </button>
            )}
            {inactiveAssigned.map((a) => (
              <button
                key={a.id}
                // Single-select: clicking a suspended assignee unassigns, never re-picks them.
                onClick={(e) => { e.stopPropagation(); multi ? toggle(a.id) : clearAll(); }}
                title={`${inactiveAccountLabel(a.accountStatus)} in Accounts. Click to unassign.`}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-grey-500 hover:bg-grey-50 transition-colors"
              >
                {multi && (
                  <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border bg-accent border-accent text-white">
                    <Check className="w-3 h-3" />
                  </span>
                )}
                <Avatar src={a.avatarUrl} name={a.name} size="xs" className="opacity-50 grayscale" />
                <span className="truncate">{a.name}</span>
                <span className="ml-auto rounded bg-error/10 px-1.5 py-0.5 text-[10px] font-medium text-error">
                  {inactiveAccountLabel(a.accountStatus)}
                </span>
              </button>
            ))}
            {projectMembers
              .filter((m) => m.user.name.toLowerCase().includes(search.toLowerCase()))
              .map((member) => {
                const checked = selectedIds.has(member.user.id);
                return (
                  <button
                    key={member.user.id}
                    onClick={(e) => { e.stopPropagation(); toggle(member.user.id); }}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-grey-50 transition-colors',
                      checked ? 'font-medium text-primary' : 'text-secondary',
                    )}
                  >
                    {multi && (
                      <span className={cn(
                        'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
                        checked ? 'bg-accent border-accent text-white' : 'border-grey-300',
                      )}>
                        {checked && <Check className="w-3 h-3" />}
                      </span>
                    )}
                    <Avatar src={member.user.avatarUrl} name={member.user.name} size="xs" />
                    {member.user.name}
                  </button>
                );
              })}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
